import type { UIUtilTime } from "../util/Time";
import { IMainFilterItemTypeConfig, TFiltroActivoValueShareMainMap, TMainFilterType } from "./FilterControlV2";

export interface ITableConfig<TDataTable> {
    /** Identificador único de tabla (Para preservar configuración de tabla en LocalStorage) */
    IdTabla: string;
    /** Contenedor padre de la tabla */
    Parent: d3.Selection<HTMLElement, any, any, any>;
    /** (Opcional) Título de la tabla */
    Title?: string;
    /** (Opcional) Llave a considerar como id por registro
     * * En caso de que cada registro tenga un Identificador único, los elementos checkeados serán conservados despues de IObjectControl.met_UpdateData().
     * * En caso contrario, así como al omitir 'IdData', cada invocación de IObjectControl.met_UpdateData()
     * reseteará los estados (IsChecked, IsCollapsed) de los items. */
    IdData?: (keyof TDataTable & string);
    ItemGrouperField?: (keyof TDataTable & string);
    /** Almacena parámetros del ordenamiento inicial de datos */
    OrderDefault?: Omit<TOrderField<TDataTable>, keyof Pick<TOrderField<TDataTable>, "IsLowLevel">>;
    /**
     * Define la configuración para la caja de búsqueda por texto
     * @example
     * "none": Sin caja de búsqueda
     * ((dato: TDataTable) => string[]): Retornar manualmente las cadenas correspondientes al dato en iteración
     * @default "default" */
    FilterByStrSearch?: ("default" | "none" | ((dato: TDataTable) => string[])),
    /** Configuracón de datos y dimenciones a mostar
     * @deprecated Use 'Columns'
    */
    RenderColumnHeadings: Array<TTableColumnOld<TDataTable>> | undefined | null;
    /** Configuración de columnas */
    Columns?: Array<TTableColumn<TDataTable>>;
    /** (Opcional) Minimo del ancho total de la Tabla.
     * * Por default desde los estilos el ancho es de 1000px */
    MinWidth?: number,
    /** (Opcional) Menú ubicado en la barra superior de la Tabla */
    OptionsTopDefaultV2?: ITableMenuTopDefaultConfig;
    /** (Opcional) Menú se ubica sobre la barra superior y sobre los encabezados ('Title', 'OptionsTopDefaultV2' si existen)
     * al tener filas Checkeadas, con comportamiendo Toggle
     * * Nota: se invoca (se actualiza ui) por cada dato checkeado/descheckeado
     * */
    OptionsOfDataCheckV3?: (dato: TDataTable[]) => ITableMenuDataSelectedConfig<TDataTable>;
    /** Cantidad de celdas de izquierda a derecha para ignorar su width en el calculo del ancho del menu en file
     * @default 1
     */
    MenuInRowNoCellsToIgnoreWidth?: number;
    /** (Opcional) Parámetros de filtro, si el arreglo tiene elementos agrega un area un area con las opciones de filtros especificados */
    FilterParameters?: Array<TTableParametroFiltro<TDataTable>>;
    /** (Opcional) Agrega a la tabla la carácteristica de pasar al inicio a los elementos chequeados
     * @default true */
    EnableRelevanceSelections?: boolean;
    /** (Opcional) Las columna de acción check se ajusta como elementos pegajosos (siempre visibles en caso de scroll horizontal)
     * @default false */
    StickyCheckInRow?: boolean;
    // StickyCollapserIndicatorInRow?: boolean;
    /** (Opcional) Permite que el usuario habilite una columna como elemento pegajoso (siempre visible en caso de scroll horizontal)
     * @default false */
    EnableStickyFields?: boolean;
    /** (Opcional) Muestra/oculta la columna de los checkbox
     * @default false */
    // NOTE Pasar a EVALUADOR DE NIVELES ?
    HideCheckboxes?: boolean;
    /** (Opcional) Al tener valor True cada celda se clasea con el nombre del campo correspondiente al objeto al que pertenece
     * @default false */
    AddNameFieldClassToCells?: boolean;
    /** @default true */
    HideItemWhenItHasNoChildItems?: boolean;
    /** Las filas deshabilitadas con derecho a tener menú de fila
     * @default false */
    DisabledRowsWithMenuInRow?: boolean;
    /** (Opcional) Se invoca al hacer click sobre una fila, agrega estilo gris claro a la celda clickeada (estilo Toggle)
     * @deprecated
    */
    // NOTE pasar a Evaluador de niveles
    OnValueSelectRow?: (idDatum: string | number, datum: TDataTable, isSelected: boolean) => void;
    EvaluatorAndSubLevelsBuild?: ITableStepEvaluator<TDataTable, any, any>;
    OnEndUpdateDataInView?: (dataFiltered: TTableDataItemTableAux<TDataTable>[], dataChecked: TTableDataItemTableAux<TDataTable>[]) => void;
    OnClickSyncTable?: () => Promise<Array<TDataTable>>;
    /** Key del "modulo" al que pertenecen los strings de la tabla:
     * * Headers
     */
    LangModuleKeyInContext?: string;
    FilterExtraBeforeFilterTable?: (dato: TDataTable) => boolean;
    OnFilter?: () => void;
    OnChangeOrder?: (field: (keyof TDataTable & string), orderType: CTableStatusOrder) => void;
    /** (Opcional) Hace que las columnas sean redimensionables
     * @default true */
    Resizable?: boolean;
    /** (Opcional) Hace que las columnas sean reordenables
     * @default true */
    Reorderable?: boolean;
}

// *********************************************************************
// BASICS
// *********************************************************************

export type TTableIdType = (number | string);

export enum CTableOriginEventType {
    OnClickRowTopCheck = 1,
    OnClickSuperCheck,
    OnExternalEvent,
    OnCloseOptionsOfDataCheck,
    OnClickChildCheck,
    /** Cuando el la actualización de los datos colapsables desencadena que el item top cambie su estado
     *
     * * Caso: Cuando los elementos colapsables checkeados son removidos en una actualización de datos
     * sin interactuar directamente con los datos
     */
    OnChildsUpdateTopCheck,
    OnUpdateDataTable
}

// *********************************************************************
// COLUMNS
// *********************************************************************

type TColumnAlignment = "start" | "end" | "center";

type TColumnType = "text" | "number" | "date"; // | "img" | "file" | "boolean"; // FIXME // FIXME // FIXME IMPLEMENTAR FALTANTES + SOPORTE PARA EXPORTAR DATOS FORMATEADOS
type TColumnAutoFormatConfig<T extends TColumnType> =
    T extends "date" ? {
        Format?: UIUtilTime.TDateStandarFormats;
    } :
    T extends "number" ? {
        Format?: "currency" | "percent"; // | "decimal_2digits";
    } : {};

type TColumnHeaderIconType = "img" | "file";
type TColumnHeaderIcon = {
    [T in TColumnHeaderIconType]: {
        /** @default "img" */
        Type: T;
    } & (T extends "img" ? {
        Src: string;
    } : {
        /** @example "PDF", "IMG", "FILE" */
        Text: string;
    })
}[TColumnHeaderIconType];

export type TTableColumnOld<TDataTable> = {
    [CT in TColumnType]: TColumnConfig<TDataTable, CT>;
}[TColumnType];

export type TTableColumn<TDataTable> = {
    [CT in TColumnType]: TTableColumnConfigV2<TDataTable, CT>;
}[TColumnType];

export type TTableColumnConfigV2<TDataTable, Type extends TColumnType = "text"> = {
    RenderCell?: (value: unknown, displayValue: string, datum: TDataTable) => string | Node;

} & TColumnConfig<TDataTable, Type>;

type TColumnConfig<TDataTable, Type extends TColumnType = "text"> = {
    /** Propiedad a MOSTRAR
     * * Puede mostrar datos de otros niveles del objeto, "CampoEjemplo.Valor" */
    Field: (keyof TDataTable & string);
    /**
     * @default "text"
     */
    Type?: Type;
    /**
     * @default
     * "start": if Type = "text"
     * "end": if Type = "number" | "date"
     * "center": if Type = "img" | "file"
     */
    Align?: TColumnAlignment;
    /**
     * * `string`: Acepta un src de icono
     * * `TColumnHeaderIcon`: Configuración especifica
     */
    Icon?: string | TColumnHeaderIcon;
    /** Texto de encabezado */
    Label: string;
    /** @default == thisconfig.Field */
    LabelLangKey?: string;
    /** Usar _% / 100% -> "10%"*/
    Width?: string;
    /** Usar px -> 100px*/
    MinWidth: string;
    /** Estilo aplicado al td (contenedor) de esta propiedad */
    ClassStyle?: string;
    /** Evento click en la celda (td) correspondiente */
    OnClickInCellCont?: (datum: TDataTable, event: MouseEvent) => void;
    /** Agrega a la cabecera la caracteristica de reordenar la columna
     * @default true */
    IsSortable?: boolean;
    /** Agrega en cabecera botón habilitador de columna pegajosa
     * @default false */
    IsStickable?: boolean;
    /** Es elemento pegajozo?
     * @default false */
    Sticked?: boolean;
    /** @default 1 */
    OrderLevelRows?: number;
    /** @default "auto" */
    OrderTypeParse?: TOrderTypeData;
    /** Propiedad para tomar en cuenta para la comparación de ORDENAMIENTO
     * * Puede mostrar datos de otros niveles del objeto, "CampoEjemplo.Valor"
     * @default this.Field
    */
    OrderField?: (keyof TDataTable & string);

    /** @default ```true``` */
    ResizableWidth?: boolean; // FIXME IMPLEMENTAR Agregar al <style> Anchura de las columnas

    OnLoadHead?: (container: TSelectionHTML<"div">) => void;
}
    & TColumnAutoFormatConfig<Type>;

// *********************************************************************
// EVALUADORES DE NIVELES
// *********************************************************************

export type ITableStepEvaluator<TData, TSubData1, TSubData2> = {
    RemoveLevelCheckboxes?: boolean;
    HideLevelCollapseIndicator?: boolean;
    EvaluatorSubLevel?: ITableStepSubLevelEvaluator<TData, TSubData1, TSubData2>;
    /** Define si el row se inicializa como colapsado, por omisión el row es colapsado */
    OnEvalIsCollapsedRow?: (data: TData, lastStatus: boolean, indexOrigin: number) => boolean;
    /** Define si el row se inicializa como habilitado, por omisión el row es habilitado */
    OnEvalIsEnableRow?: (dato: TData, lastStatus: boolean, indexOrigin: number) => boolean;

    /** Nota: En el caso de los subniveles se invoca con las filas colapsables */
    OnStepCellTable?: (container: TSelectionHTML<"div">, datum: TData, fieldNameStep: (keyof TData & string) | string, /* typeStep: TTypeStep, */ indexInicialOfDatum?: number) => void;
    /** Nota: En el caso de los subniveles se invoca con las filas colapsables */ // SOBREESCRITO
    OnStepRowTable?: (datum: TData, tableRow: TSelectionHTML<"tr", any>, rowBody: TSelectionHTML<"div", any>, indexOrigin: number, parentData: undefined, restOfParentsData: []) => void; // ctrlExtraFeatures: IExtraRowFeatures<TDataTable>) => void

    OnCollapserChange?: (isCollapsed: boolean, dato: TData, rowBodyParent: TSelectionHTML<"div", TDataItemTable<TData>>) => void;

    /** Por cada checkeo o descheckeo realizado se ejecuta (cada vez que el número de datos checkeados cambia) */  // SOBREESCRITO
    OnCheckedData?: (dataChecked: Array<TData>, originEvent: CTableOriginEventType, parentData: null, restOfParentsData: null) => void;

    /** Menú sobre la fila seleccionada (al hacer click sobre la fila) con comportamiento Toggle */
    GetOptionsInRowV2?: (dato: TData) => ITableMenuDataSelectedConfig<TData>;

    /** Agrega lineas extras a la fila correspondiente */
    AddRowMultiline?: ITableRowMultilineConfig<TData, any> // <TData>(config: IRowMultilineConfig<TData>) => void;

    /** @default false */
    ShowNoContentTagRow?: boolean;
} & IStepEvaluatorDataEval;

type IStepEvaluatorDataEvalAux = { 1: true, 2: false };
type IStepEvaluatorDataEval = {
    [k in keyof IStepEvaluatorDataEvalAux]: {
        /** Define el momento en que se invoca {@link ITableStepSubLevelEvaluator.OnGetData}
         * * `true`: La funcion {@link ITableStepSubLevelEvaluator.OnGetData OnGetData} del nivel inferior se ejecutará solo cuando se descolapse la la fila de éste nivel.
         *      Si {@link ITableStepSubLevelEvaluator.OnGetData OnGetData} retorna una promesa:
         *      * Agrega un spinner de carga a la fila
         *      * Las filas sin childs no se ocultan
         * * `false`: invoca {@link ITableStepSubLevelEvaluator.OnGetData OnGetData} al pasar data a la tabla (no recomendado con Promesas)
         *      * Por defecto las filas sin childs no se ocultan (se modifica con `ForceShowLevelRows`)
         *  @default false
         * */
        ActiveDataOnlyWhenDisplayData?: IStepEvaluatorDataEvalAux[k];
    } & (IStepEvaluatorDataEvalAux[k] extends true ? {
        /** Válido solo cuando `ActiveDataOnlyWhenDisplayData == (false | null | undefined)` */
        ForceHideLevelRowsWithoutChilds?: false;
    } : {
        /** Válido solo cuando `ActiveDataOnlyWhenDisplayData == (false | null | undefined)` */
        ForceHideLevelRowsWithoutChilds?: true;
    })
}[keyof IStepEvaluatorDataEvalAux]

export type ITableStepSubLevelEvaluator<TDataParent, TData, TSubData1> = {
    /** Si es (null | undefined), se toma el valor de "IdData" de la configuración de la tabla.
     *
     * Si el valor del dato no es encontrado, cada vez que se actualicen los datos colapsables, sus estados (IsChecked, IsCollapsed) serán reseteados
     */
    IdMember: string;
    /** Define los datos colapsables correspondientes al dato
     * @param dato Padre inmediado
     * @param parentsDataSuper Resto de los padres superiores
    */
    /** Solo debe retornar Promise cuando ActiveDataOnlyWhenDisplayData del nivel superior es == true */
    OnGetData: (dato: TDataParent, parentsDataSuper: any[]) => (TData[] | Promise<TData[] | Error>);

    OnClickCheck?: (itemDato: TData, isChecked: boolean, /** allChildrenChecked: TSubData1[] parentsData: any[] */) => void,

    // SOBREESCRITOS ***************

    /** Nota: En el caso de los subniveles se invoca con las filas colapsables */
    OnStepRowTable?: (datum: TData, tableRow: TSelectionHTML<"tr", any>, rowBody: TSelectionHTML<"div", any>, indexOrigin: number, parentData: TDataParent, restOfParentsData: any[]/* [TDataParent, ...any]*/) => void; // ctrlExtraFeatures: IExtraRowFeatures<TDataTable>) => void
    /** Por cada checkeo o descheckeo realizado se ejecuta (cada vez que el número de datos checkeados cambia)
     * * @param parentsData los datos padres de los checheados comienza por el más cercano [0] == parent1 ...
     * * // NOTE // REMOVER?
     * */
    OnCheckedData?: (dataChecked: Array<TData>, originEvent: CTableOriginEventType, parentData: TDataParent, restOfParentsData: any[]) => void;
} & ITableStepEvaluator<TData, TSubData1, any>


export interface ITableRowMultilineConfig<TParentData, TData> {
    OnGetDataToAddRow: (dataParent: TParentData) => TData[],
    OnStepCell?: (tdContent: TSelectionHTML<"div">, datum: TData, field: string, parentData: TParentData, restOfParentsData: any[] /*, typeStep: TTypeStep*/) => void;
    OnStepNewTrs?: (d: TData, tr: TSelectionHTML<"tr">, parentData: TParentData, restOfParentsData: any[] /* [TParentData, ...any] */ /* typeStep: TTypeStep*/) => boolean;
}

// *********************************************************************
// ORDER
// *********************************************************************

type TOrderTypeData = StringConstructor | NumberConstructor | DateConstructor | BooleanConstructor | "auto";

type TOrderField<T = any> = {
    Type: CTableStatusOrder;
    Field: keyof T & string;
    IsLowLevel: boolean;
}

export enum CTableStatusOrder {
    Asc = 1,
    Desc,
    None
}

// *********************************************************************
// MENU TOP DATA SELECTED
// *********************************************************************

export interface ITableMenuDataSelectedConfig<TData = Object> {
    /** @default 4 */
    MaxOptionsInRow?: number;
    Options: Array<TTableMenuDataSelectedOptionConfig<TData>>;
}

export type TTableMenuDataSelectedOptionConfig<TData = Object> = {
    Label: string;
    /**
     * * `True` Opcion habilitado en multiselect, deshabilitado en 1 select
     * * `False` Opcion deshabilitado en multiselect, habilitado en 1 select
     * @default null siempre habilitado
     */
    MultiData?: boolean;
    Callback: (data: TData[]) => void | Function | Promise<void>;
    /**
     * @returns
     * * `boolean` (Habilita/Deshabilita)
     * * `ITableMenuDataDetails` (Opciones avanzadas)
    */
    GetDetails?: (data: Array<TData>) => (boolean | ITableMenuDataDetails);
}

// *********************************************************************
// MENUS
// *********************************************************************

interface ITableMenuDataDetails {
    Enabled?: boolean;
    Description?: string;
}

// *********************************************************************
// MENU TOP DEFAULT
// *********************************************************************

export interface ITableMenuTopDefaultConfig {
    /** @default 4 */
    MaxOptionsInRow?: number;
    Options: Array<TTableMenuTopDefaultOptionConfig>;
}

export type TTableMenuTopDefaultOptionConfig = {
    Label: string;
    Callback: () => void | Function | Promise<void>;
    /**
     * @returns
     * * `boolean` (Habilita/Deshabilita)
     * * `ITableMenuDataDetails` (Opciones avanzadas)
    */
    GetDetails?: () => (boolean | ITableMenuDataDetails);
}

// *********************************************************************
// DATOS DE LA TABLA
// *********************************************************************

export type TDataItemTable<TData, TDataChild = any> = {
    // key: number;
    readonly id: TTableIdType;
    readonly data: TData;
    // dataView: { [k in keyof TData]: any }; // FIXME IMPLEMENTAR, Funcionará para ExcelExport, Busquedas, Filtros, ViewDeTabla // FIXME
    isChecked: boolean;
    // isRowSelected: boolean;
    readonly initialPos: number;
    isRowEnable: boolean;
    readonly levelRow: number;
    isCollapsed: boolean;
    childDataOriginMap: Map<TTableIdType, TDataItemTable<TDataChild>>;
    childDataFilteredArray: Array<TDataItemTable<TDataChild>>;
    itemParent: TDataItemTable<any>;
}

export type TTableDataItemTableAux<TData, TDataChild = any> = {
    readonly [k in keyof TDataItemTable<TData, TDataChild>]: TDataItemTable<TData, TDataChild>[k];
}

export interface ITableDataCheckedAdvanced<TData, TData2 = any, TData3 = any, TData4 = any> {
    Data: TData;
    IsChecked: boolean;
    ChildsChecked: ITableDataCheckedAdvanced<TData2, TData3, TData4>[];
}

// *********************************************************************
// FILTROS
// *********************************************************************

export type ITableParametroFiltroMap<TData, KTipoFiltro extends TMainFilterType> = {
    /** Ejs: "Nombre" | "Objeto.Nombre" | ... */
    Field: (keyof TData & string);
    Label?: string;
    /** @default == thisconfig.Field */
    LabelLangKey?: string;
    /** if == null -> No search Lang context
     * @default config.LangModuleKeyInContext */
    LangModuleKeyInContext?: string;
    /** @default "text" */
    Type?: KTipoFiltro;
    /** Aplica solo para la conf de la Tabla
     * @default 1
    */
    Level?: number;
    OnGetValueToMatch?: (dato: TData) => Array<
        KTipoFiltro extends "number" ? number
        : KTipoFiltro extends "date" ? (string | Date)
        : KTipoFiltro extends "text" ? string
        : any
    >;
    OnChange?: (filtro: TFiltroActivoValueShareMainMap[KTipoFiltro]) => void;
} & Omit<IMainFilterItemTypeConfig<KTipoFiltro>, keyof Pick<IMainFilterItemTypeConfig<"text">, "Type" | "Key" | "Label" | "OnGetValueData">>;

export type TTableParametroFiltro<TData = Object> = {
    [KFilterType in TMainFilterType]: ITableParametroFiltroMap<TData, KFilterType>;
}[TMainFilterType];